import { assertNotEmpty } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'

import { useTemplateContext } from '../../context/templateContext'
import { templateIdFromPath } from '../../uiModels/templateOperations/helpers'
import {
  type InheritableField,
  isInherited,
} from '../../uiModels/templateOperations/types'
import { TemplatePath } from '../path/TemplatePath'
import { TemplateParentLink } from '../templateParentLink/TemplateParentLink'

type Props<T extends InheritableField> = {
  value: T
  className?: string
}

export const InheritanceIndicator = <T extends InheritableField>({
  value: { path, origin },
  className,
}: Props<T>) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()

  assertNotEmpty(templateVersion)

  const fieldOriginTemplateId = origin
    ? templateIdFromPath(origin)
    : templateVersion.versionInfo.identifier
  const areFieldsFromCurrentTemplate =
    templateVersion.versionInfo.identifier === fieldOriginTemplateId
  const isFieldInherited = isInherited({ origin, path })

  return isFieldInherited ? (
    <Tooltip
      tip={
        areFieldsFromCurrentTemplate ? (
          <div className="flex flex-col gap-y-1">
            <div className="font-medium">
              {translate('templateViewer.inheritanceIndicator.currentPath')}
            </div>
            <div>
              <TemplatePath
                block={templateVersion.block}
                layer={templateVersion.layer}
                value={path}
                shouldRenderTemplateId
                isInteractive={false}
              />
            </div>
            <div className="font-medium">
              {translate(
                'templateViewer.inheritanceIndicator.inheritsFromPath',
              )}
            </div>
            <div>
              <TemplatePath
                block={templateVersion.block}
                layer={templateVersion.layer}
                value={origin ?? ''}
                shouldRenderTemplateId
                isInteractive={false}
              />
            </div>
          </div>
        ) : (
          <TemplateParentLink templateId={fieldOriginTemplateId} />
        )
      }
      placement="bottom"
    >
      <Icon name="BarsArrowUp" className={className} />
    </Tooltip>
  ) : null
}
